import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const STATUS_DOT: Record<string, string> = { online: "#1f8a5b", away: "#c0612f", offline: "#bcbfba" };
const OUTCOME_TONE: Record<string, { bg: string; fg: string }> = {
  ok: { bg: "#e8f4ee", fg: "#1f7a4d" },
  warn: { bg: "#fbf1dd", fg: "#9a7b1f" },
  bad: { bg: "#f7e4e0", fg: "#b3261e" },
  neutral: { bg: "#f0efe9", fg: "#6b706a" },
};
function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / (60 * 24))}d ago`;
}

export function Team() {
  const { data, isLoading, error } = useQuery({ queryKey: ["team"], queryFn: api.team });
  const routing = useQuery({ queryKey: ["router-log"], queryFn: api.routerLog });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Team</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Offered vs accepted is the signal that separates hard workers from ticket-dodgers.
        </div>
      </div>

      <TeamMembers />

      <div>
        <h2 style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 600 }}>Performance</h2>
      </div>
      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr .9fr .7fr .7fr .8fr .7fr .6fr .7fr", padding: "10px 18px", fontSize: 10.5, fontWeight: 600, letterSpacing: ".8px", textTransform: "uppercase", color: "#a8aca6", borderBottom: "1px solid #f4f3ee" }}>
          <span>Rep</span><span style={{ textAlign: "right" }}>Accept</span><span style={{ textAlign: "right" }}>Solved</span><span style={{ textAlign: "right" }}>1st reply</span><span style={{ textAlign: "right" }}>Handle</span><span style={{ textAlign: "right" }}>SLA</span><span style={{ textAlign: "right" }}>CSAT</span><span style={{ textAlign: "right" }}>Timed out</span>
        </div>
        {data.map((r) => (
          <div key={r.name} style={{ display: "grid", gridTemplateColumns: "1.4fr .9fr .7fr .7fr .8fr .7fr .6fr .7fr", alignItems: "center", padding: "12px 18px", fontSize: 13, borderTop: "1px solid #f4f3ee" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11 }}>{r.initials}</span>
              <span>
                <span style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_DOT[r.status] ?? "#bcbfba" }} />{r.name}
                </span>
                <span style={{ fontSize: 11, color: "#8a8e88" }}>{r.active} active</span>
              </span>
            </span>
            <span style={{ textAlign: "right" }}>
              <span style={{ fontFamily: "'Space Mono',monospace", fontWeight: 600, color: r.acceptRate >= 90 ? "#1f8a5b" : r.acceptRate >= 75 ? "#9a6a12" : "#b3261e" }}>{r.acceptRate}%</span>
              <span style={{ fontSize: 11, color: "#8a8e88", display: "block" }}>{r.accepted}/{r.offered}</span>
            </span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.solved}</span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.firstReply}</span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.handle}</span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace", color: r.slaMet >= 90 ? "#1f8a5b" : "#9a6a12" }}>{r.slaMet}%</span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.csat}</span>
            <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace", color: r.timedOut > 10 ? "#b3261e" : "#8a8e88" }}>{r.timedOut}</span>
          </div>
        ))}
      </div>

      {/* Routing log (§4.2) — the RouterDO's offer/accept/timeout/escalate trail. */}
      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "13px 18px 10px" }}>
          <Icon name="git-branch" size={15} color="#006747" />
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Routing log</h2>
          <span style={{ fontSize: 11.5, color: "#8a8e88" }}>offers, accepts, timeouts & escalations</span>
        </div>
        {routing.data?.log.map((e) => {
          const tone = OUTCOME_TONE[e.tone ?? "neutral"] ?? { bg: "#f0efe9", fg: "#6b706a" };
          return (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
              <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#8a8e88", minWidth: 66 }}>{e.ref}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: "#f0efe9", color: "#6b706a", fontFamily: "'Space Mono',monospace" }}>T{e.tier}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg }}>{e.outcome.replace("_", " ")}</span>
              <span style={{ flex: 1, color: "#4a504a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.rep ?? "—"}</span>
              <span style={{ color: "#8a8e88", whiteSpace: "nowrap" }}>{ago(e.at)}</span>
            </div>
          );
        })}
        {routing.data && routing.data.log.length === 0 && <div style={{ padding: "10px 18px", fontSize: 12.5, color: "#8a8e88", borderTop: "1px solid #f4f3ee" }}>No routing activity yet.</div>}
      </div>
    </Page>
  );
}

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" };
const FIELD: React.CSSProperties = { fontSize: 12.5, padding: "7px 9px", border: "1px solid #e2e0d8", borderRadius: 6, background: "#fff", color: "#1d241f" };

// Staff identity roster — add teammates, set their role + admin, and assign
// mailboxes. Writes are gated by `canManage` (server re-checks users.manage).
function TeamMembers() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["team-members"], queryFn: api.teamMembers });
  const [showAdd, setShowAdd] = useState(false);
  const [openMbx, setOpenMbx] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", roleSlug: "agent", isAdmin: false });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["team-members"] });
  const save = useMutation({
    mutationFn: (v: { id: string; patch: { roleSlug?: string; isAdmin?: boolean; mailboxIds?: string[] } }) => api.updateTeamMember(v.id, v.patch),
    onSuccess: invalidate,
  });
  const add = useMutation({
    mutationFn: () => api.addTeamMember({ name: form.name.trim(), email: form.email.trim(), roleSlug: form.roleSlug, isAdmin: form.isAdmin }),
    onSuccess: () => { invalidate(); setShowAdd(false); setForm({ name: "", email: "", roleSlug: "agent", isAdmin: false }); },
  });

  if (q.isLoading || !q.data) return null;
  const { canManage, viewerIsAdmin, roles, mailboxes, members } = q.data;
  const roleName = (slug: string | null) => roles.find((r) => r.slug === slug)?.name ?? (slug ?? "—");
  const canAdd = form.name.trim().length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) && !add.isPending;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Team members</h2>
        <span style={{ fontSize: 11.5, color: "#8a8e88", flex: 1 }}>{members.length} {members.length === 1 ? "person" : "people"} · they sign in via Cloudflare Access; this sets their in-app role & mailboxes</span>
        {canManage && (
          <button onClick={() => setShowAdd((v) => !v)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 8, padding: "7px 12px", cursor: "pointer" }}>
            <Icon name="user-plus" size={14} color="#fff" /> Add teammate
          </button>
        )}
      </div>

      {showAdd && canManage && (
        <div style={{ ...CARD, padding: 14, marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" style={{ ...FIELD, flex: "1 1 160px" }} />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="work email" style={{ ...FIELD, flex: "1 1 200px" }} />
          {/* Role + admin are admin-only; a users.manage manager onboards a plain agent. */}
          {viewerIsAdmin && (
            <>
              <select value={form.roleSlug} onChange={(e) => setForm({ ...form, roleSlug: e.target.value })} style={FIELD}>
                {roles.map((r) => <option key={r.id} value={r.slug}>{r.name}</option>)}
              </select>
              <label style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6, color: "#4a504a" }}>
                <input type="checkbox" checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} /> Admin
              </label>
            </>
          )}
          <button onClick={() => canAdd && add.mutate()} disabled={!canAdd} style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: canAdd ? "#006747" : "#b7c3bd", border: 0, borderRadius: 8, padding: "8px 14px", cursor: canAdd ? "pointer" : "default" }}>
            {add.isPending ? "Adding…" : "Add"}
          </button>
          {add.isError ? (
            <span style={{ fontSize: 11.5, color: "#b3261e" }}>{add.error instanceof Error ? add.error.message : "Couldn't add"}</span>
          ) : !canAdd && !add.isPending ? (
            <span style={{ fontSize: 11.5, color: "#8a8e88" }}>Enter a name and a valid work email.</span>
          ) : null}
        </div>
      )}

      <div style={CARD}>
        {save.isError && <div style={{ padding: "8px 18px", fontSize: 11.5, color: "#b3261e", background: "#fbe9e7", borderBottom: "1px solid #f3d3ce" }}>{save.error instanceof Error ? save.error.message : "Couldn't save"}</div>}
        {members.map((m) => (
          <div key={m.id} style={{ borderTop: "1px solid #f4f3ee" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr auto auto", alignItems: "center", gap: 10, padding: "11px 18px", fontSize: 13 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11, flex: "none" }}>{m.initials}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, display: "block" }}>{m.name}</span>
                  <span style={{ fontSize: 11, color: "#8a8e88", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</span>
                </span>
              </span>
              {viewerIsAdmin ? (
                <select value={m.roleSlug ?? ""} disabled={save.isPending} onChange={(e) => save.mutate({ id: m.id, patch: { roleSlug: e.target.value } })} style={{ ...FIELD, padding: "5px 8px", cursor: save.isPending ? "default" : "pointer" }}>
                  {m.roleSlug == null && <option value="">— no role —</option>}
                  {roles.map((r) => <option key={r.id} value={r.slug}>{r.name}</option>)}
                </select>
              ) : (
                <span style={{ fontSize: 12.5, color: "#4a504a" }}>{roleName(m.roleSlug)}</span>
              )}
              {/* Admin flag + role are admin-only (above users.manage); a users.manage manager sees them read-only. */}
              <label style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, color: viewerIsAdmin ? "#4a504a" : "#a8aca6", justifySelf: "start" }}>
                <input type="checkbox" checked={m.isAdmin} disabled={!viewerIsAdmin || save.isPending} onChange={(e) => save.mutate({ id: m.id, patch: { isAdmin: e.target.checked } })} /> Admin
              </label>
              <button
                onClick={() => setOpenMbx(openMbx === m.id ? null : m.id)}
                aria-expanded={openMbx === m.id}
                title={`${openMbx === m.id ? "Hide" : "Edit"} mailbox assignments for ${m.name}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#006747", background: "none", border: "1px solid #d9e3dd", borderRadius: 8, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {m.mailboxIds.length} {m.mailboxIds.length === 1 ? "mailbox" : "mailboxes"}
                <Icon name="chevron-down" size={13} color="#006747" style={{ transform: openMbx === m.id ? "rotate(180deg)" : "none", transition: "transform .12s" }} />
              </button>
            </div>
            {openMbx === m.id && (
              <div style={{ padding: "0 18px 12px 57px", display: "flex", flexWrap: "wrap", gap: 12 }}>
                {mailboxes.length === 0 && <span style={{ fontSize: 12, color: "#8a8e88" }}>No mailboxes configured yet.</span>}
                {mailboxes.map((mb) => {
                  const on = m.mailboxIds.includes(mb.id);
                  return (
                    <label key={mb.id} style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6, color: "#4a504a" }}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!canManage || save.isPending}
                        onChange={() => {
                          const next = on ? m.mailboxIds.filter((x) => x !== mb.id) : [...m.mailboxIds, mb.id];
                          save.mutate({ id: m.id, patch: { mailboxIds: next } });
                        }}
                      />
                      {mb.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {members.length === 0 && <div style={{ padding: "14px 18px", fontSize: 12.5, color: "#8a8e88" }}>No teammates yet.</div>}
      </div>
    </div>
  );
}
